"use client"

import { create } from "zustand"
import { persist } from "zustand/middleware"
import { useEffect } from "react"
import { toast } from "sonner"
import type {
  AuthUser,
  LoginPayload,
  RegisterPayload,
  TokenPair,
} from "@/lib/api/auth"
import {
  loginApi,
  registerApi,
  logoutApi,
  getMeApi,
  refreshApi,
  resendVerificationApi,
  verifyEmailApi,
  updateProfileApi,
} from "@/lib/api/auth"
import { apiClient } from "@/lib/api/client"
import type { ApiErrorResponse } from "@/lib/errors/types"
import { deleteCookie } from "@/utils/cookies"
import {
  setAuthSessionCookie,
  clearAuthSessionCookie,
} from "@/lib/auth/session-cookies"
import { useUserInfoStore } from "./use-user-info"
import { useVisitorQuotesStore } from "@/stores/visitor-quotes"

interface AuthState {
  user: AuthUser | null
  tokens: TokenPair | null
  isLoading: boolean
  error: string | null
}

interface AuthActions {
  login: (payload: LoginPayload) => Promise<void>
  register: (payload: RegisterPayload) => Promise<void>
  resendVerification: (email: string) => Promise<void>
  verifyEmail: (email: string, code: string) => Promise<void>
  logout: () => Promise<void>
  fetchMe: () => Promise<void>
  refresh: () => Promise<boolean>
  updateProfile: (name: string) => Promise<void>
  clearError: () => void
  validateSession: () => Promise<boolean>
  clearSession: () => void
}

const STORAGE_KEY = "nova_auth"
const VISITOR_QUOTE_COOKIE_KEY = "nova_visitor_quote_id"

async function syncAuthCookies(
  accessToken: string,
  expiresIn: number,
  role?: string
) {
  await setAuthSessionCookie({
    accessToken,
    maxAge: expiresIn,
    role,
  })
}

/** Clear HttpOnly session cookies, web storage, related stores, and in-memory Bearer. */
async function clearClientAuthState() {
  await clearAuthSessionCookie()
  deleteCookie(VISITOR_QUOTE_COOKIE_KEY)
  localStorage.clear()
  sessionStorage.clear()
  useUserInfoStore.getState().reset()
  useVisitorQuotesStore.getState().reset()
  apiClient.setAuthToken(null)
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      isLoading: false,
      error: null,

      login: async (payload) => {
        set({ isLoading: true, error: null })
        try {
          const res = await loginApi(payload)
          const { user, ...tokens } = res.data
          apiClient.setAuthToken(tokens.access_token)
          await syncAuthCookies(
            tokens.access_token,
            tokens.expires_in,
            user?.role
          )
          set({ user, tokens, isLoading: false })
          toast.success("Welcome back!", {
            description: "You have successfully logged in.",
          })
        } catch (err: unknown) {
          const apiError = err as ApiErrorResponse
          const message = apiError?.error?.message ?? "Login failed"
          set({ isLoading: false, error: message })
          throw err
        }
      },

      register: async (payload) => {
        set({ isLoading: true, error: null })
        try {
          await registerApi(payload)
          set({ isLoading: false })
        } catch (err: unknown) {
          const apiError = err as ApiErrorResponse
          const message = apiError?.error?.message ?? "Registration failed"
          set({ isLoading: false, error: message })
          throw err
        }
      },

      resendVerification: async (email) => {
        set({ error: null })
        try {
          await resendVerificationApi(email)
        } catch (err: unknown) {
          const apiError = err as ApiErrorResponse
          const message = apiError?.error?.message ?? "Failed to resend code"
          set({ error: message })
          throw err
        }
      },

      verifyEmail: async (email, code) => {
        set({ isLoading: true, error: null })
        try {
          const res = await verifyEmailApi(email, code)
          const { user, ...tokens } = res.data
          apiClient.setAuthToken(tokens.access_token)
          await syncAuthCookies(
            tokens.access_token,
            tokens.expires_in,
            user?.role
          )
          set({ user, tokens, isLoading: false })
          toast.success("Email verified!", {
            description: "Your email has been verified successfully.",
          })
        } catch (err: unknown) {
          const apiError = err as ApiErrorResponse
          const message = apiError?.error?.message ?? "Verification failed"
          set({ isLoading: false, error: message })
          throw err
        }
      },

      logout: async () => {
        const { tokens } = get()
        set({ isLoading: true })
        try {
          if (tokens?.access_token) {
            await logoutApi(tokens.access_token)
          }
        } catch {
          // Ignore API errors during logout
        } finally {
          await clearClientAuthState()
          set({ user: null, tokens: null, isLoading: false, error: null })
          toast.success("Logged out", {
            description: "You have been successfully logged out.",
          })
        }
      },

      fetchMe: async () => {
        const { tokens } = get()
        if (!tokens?.access_token) return
        set({ isLoading: true })
        try {
          const res = await getMeApi(tokens.access_token)
          set({ user: res.data, isLoading: false })
        } catch (err: unknown) {
          const apiError = err as ApiErrorResponse
          if (
            apiError?.error?.code === "AUTH_TOKEN_EXPIRED" ||
            apiError?.error?.code === "AUTH_SESSION_EXPIRED" ||
            apiError?.error?.code === "AUTH_UNAUTHORIZED"
          ) {
            await clearClientAuthState()
            set({ user: null, tokens: null, isLoading: false })
          } else {
            set({ isLoading: false })
          }
        }
      },

      refresh: async () => {
        const { tokens, user } = get()
        if (!tokens?.refresh_token) return false
        try {
          const res = await refreshApi(tokens.refresh_token)
          apiClient.setAuthToken(res.data.access_token)
          await syncAuthCookies(
            res.data.access_token,
            res.data.expires_in,
            user?.role
          )
          set({ tokens: res.data })
          return true
        } catch {
          await clearClientAuthState()
          set({ user: null, tokens: null })
          return false
        }
      },

      updateProfile: async (name) => {
        set({ error: null })
        try {
          const res = await updateProfileApi({ name })
          set((state) => ({
            user: state.user
              ? { ...state.user, name: res.data.name }
              : state.user,
          }))
          toast.success("Profile updated")
        } catch (err: unknown) {
          const apiError = err as ApiErrorResponse
          const message = apiError?.error?.message ?? "Failed to update profile"
          set({ error: message })
          toast.error(message)
          throw err
        }
      },

      clearError: () => set({ error: null }),

      validateSession: async () => {
        const { tokens } = get()
        if (!tokens?.access_token) {
          return false
        }
        try {
          await getMeApi(tokens.access_token)
          return true
        } catch (err: unknown) {
          const apiError = err as ApiErrorResponse
          if (
            apiError?.error?.code === "AUTH_TOKEN_EXPIRED" ||
            apiError?.error?.code === "AUTH_SESSION_EXPIRED" ||
            apiError?.error?.code === "AUTH_UNAUTHORIZED"
          ) {
            await clearClientAuthState()
            set({ user: null, tokens: null, isLoading: false })
            return false
          }
          // Treat session as valid on network errors or rate limits (e.g., 429) to prevent unintended logout
          return true
        }
      },

      clearSession: () => {
        void clearClientAuthState()
        set({ user: null, tokens: null, isLoading: false, error: null })
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({ user: state.user, tokens: state.tokens }),
    }
  )
)

export function useIsAuthenticated() {
  return useAuthStore((s) => !!s.tokens?.access_token)
}

export function useCurrentUser() {
  return useAuthStore((s) => s.user)
}

export function useAuthSync() {
  const clearSession = useAuthStore((s) => s.clearSession)
  const tokens = useAuthStore((s) => s.tokens)
  const user = useAuthStore((s) => s.user)

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        if (e.newValue === null) {
          clearSession()
          window.location.href = window.location.pathname.includes("/ar/")
            ? "/ar"
            : "/en"
        }
      }
    }

    // HttpOnly cookies cannot be read via document.cookie — re-assert from
    // in-memory/persisted tokens so proxy route guards stay in sync.
    const assertAuthCookie = () => {
      if (tokens?.access_token) {
        void syncAuthCookies(
          tokens.access_token,
          tokens.expires_in,
          user?.role
        ).catch(() => {
          // Re-assert is best-effort; login/refresh already require success.
        })
      } else {
        void clearAuthSessionCookie()
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        assertAuthCookie()
      }
    }

    assertAuthCookie()

    window.addEventListener("storage", handleStorageChange)
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      window.removeEventListener("storage", handleStorageChange)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [tokens, user?.role, clearSession])
}
